import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useMemo } from 'react';
import type { Coordinates } from '../lib/parse-coordinates';
import { geocodingSearchQuery } from '../api/queries';

export interface GeocodingResult {
  coordinates: Coordinates | null;
  displayName: string | null;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Calls the backend geocoding endpoint for a free-text query.
 *
 * - Only fires when `query` is a non-empty string.
 * - Returns coordinates from the geocoded result, or null on failure.
 * - Results are cached indefinitely (geocoded addresses do not change).
 */
export function useGeocodingSearch(query: string): GeocodingResult {
  const { auth } = useRouteContext({ from: '__root__' });

  const trimmed = query.trim();
  const enabled = trimmed.length > 0;

  const { data, isLoading, isError } = useQuery({
    ...geocodingSearchQuery(auth, trimmed),
    enabled,
  });

  // Stable object identity — prevents downstream hooks/effects from re-firing on every render.
  const coordinates = useMemo<Coordinates | null>(
    () => (data ? { lat: data.lat, lng: data.lng } : null),
    [data?.lat, data?.lng],
  );

  if (!data) {
    return { coordinates: null, displayName: null, isLoading: enabled && isLoading, isError };
  }

  return {
    coordinates,
    displayName: data.displayName,
    isLoading: false,
    isError,
  };
}
